<script setup>
/**
 * FormControlAccountSettingsReorderScripts
 * Utility component providing maintenance actions like resetting display orders,
 * auditing file usage, and purging system caches.
 */
import {computed, onMounted} from 'vue'
import Button from '@/common/Button.vue'
import api from '@/services/api'
import {useToastsStore} from '@/stores/toasts'
import {useUserStore} from '@/stores/user'
import {useModalsStore} from '@/stores/modals'
import {useAppStore} from '@/stores/appStore'

const props = defineProps({
    field: {
        type: Object,
        required: true
    }
})

const toastsStore = useToastsStore()
const userStore = useUserStore()
const modalsStore = useModalsStore()
const appStore = useAppStore()

const isAdmin = computed(() => userStore.user?.administrator)

/**
 * Helper to toggle global wait cursor during async operations
 */
const setWaitCursor = (isWaiting) => {
    document.body.style.cursor = isWaiting ? 'wait' : 'initial'
}

const resetDisplayOrders = async () => {
    setWaitCursor(true)
    try {
        await api.get('api/reset-all-display-orders')
        toastsStore.add({
            title: 'Reset display orders',
            body: '<p>Complete!</p><strong>Please refresh your browser.</strong>'
        })
    } catch (error) {
        console.error('Failed to reset display orders:', error)
    } finally {
        setWaitCursor(false)
    }
}

const resetFileUsage = async () => {
    setWaitCursor(true)
    try {
        await api.get('api/reset-file-usage')
        await toastsStore.add({
            title: 'Reset file usage',
            body: '<p>Complete!</p><strong>Please refresh your browser.</strong>'
        })
    } catch (error) {
        console.error('Failed to reset file usage:', error)
    } finally {
        setWaitCursor(false)
    }
}

const purgeCache = () => {
    modalsStore.show({
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: 'Are you sure?',
            modalBody: `
                <div class="alert alert-danger">
                    This will negatively impact the performance of the site until the caches are rebuilt.
                </div>
                <p>This action should be a last resort.</p>
            `,
            onConfirm: async () => {
                setWaitCursor(true)
                try {
                    await api.get('api/purge-cache')

                    const notification = await toastsStore.notification({
                        title: 'Clear the cache',
                        message: 'Starting&hellip;'
                    })

                    await appStore.clearCache({
                        notificationId: notification.notificationId
                    })
                } catch (err) {
                    console.error('Purge cache failed:', err)
                } finally {
                    setWaitCursor(false)
                }
            }
        }
    })
}

onMounted(() => {
    props.field.label = 'Reset'
})
</script>

<template>
    <div class="d-flex gap-2">
        <Button class="border" @click="resetDisplayOrders">Display orders</Button>
        <Button class="border" @click="resetFileUsage">File usage</Button>
        <Button class="border" v-if="isAdmin" @click="purgeCache">Purge cache</Button>
    </div>
</template>

<style scoped>
.gap-2 {
    gap: 0.5rem;
}
</style>